import React, { useEffect, useMemo, useState } from 'react';
import { apiProvider } from '@process-advisor/process-miner/api/apiProvider';
import { Event } from '@process-advisor/process-miner/api/events';
import { getModelID, getProcessModelTitle, getAppVersion } from '@process-advisor/process-miner/processMiner/processMiner';
import { SnapshotListPanel } from '@process-advisor/process-miner/consultant/ConsultantSnapshotManager';
import { listSnapshots } from '@process-advisor/process-miner/consultant/consultantSnapshotService';
import { getAIModelConfig } from '@advisor-core/lib/aiModelConfig';

import QuickHealthcheck  from '@process-advisor/process-miner/consultant/quickHealthcheck';
import ProcessHealth     from '@process-advisor/process-miner/consultant/processHealth';
import TrendAnalysis    from '@process-advisor/process-miner/consultant/trendAnalysis';
import AIIntelligence    from '@process-advisor/process-miner/consultant/aiIntelligence';
import AIExecutionStrategy from '@process-advisor/process-miner/consultant/aiExecutionStrategy';
import AutomationPacks   from '@process-advisor/process-miner/consultant/automationPacks';
import ConsultingReport  from '@process-advisor/process-miner/consultant/consultingReport';
import ProcessSnapshot   from '@process-advisor/process-miner/consultant/processSnapshot';
import FrameworkAssessment from '@process-advisor/process-miner/consultant/frameworkAssessment';
import ConsultingWorkspace from '@process-advisor/process-miner/consultant/ConsultingWorkspace';
import ProcessInvestigations from '@process-advisor/process-miner/consultant/ProcessInvestigations';

import {
  Activity,
  TrendingUp,
  BrainCircuit,
  ListChecks,
  Package,
  FileText,
  LayoutDashboard,
  ShieldCheck,
  HeartPulse,
  BookOpen,
  Users,
  Lightbulb,
  CheckSquare,
  Search,
} from 'lucide-react';

const TABS = [
  {
    id:          'quickHealthcheck',
    label:       'Quick Healthcheck',
    icon:        HeartPulse,
    color:       '#0C66E4',
    description: 'A short AI read for new engagements and quick client conversations — what\'s working, what\'s not, top risks, quick wins.',
  },
  {
    id:          'processHealth',
    label:       'Process Health',
    icon:        Activity,
    color:       '#0052CC',
    description: 'AI-generated executive assessment of process health, key discovery rules, and leadership priorities.',
  },
  {
    id:          'trendAnalysis',
    label:       'Trend Analysis',
    icon:        TrendingUp,
    color:       '#0C66E4',
    description: 'Period-over-period comparison — measure whether the process is improving, identify what changed, and understand what drove it.',
  },
  {
    id:          'aiIntelligence',
    label:       'AI, Automation & Optimisation',
    icon:        BrainCircuit,
    color:       '#0C66E4',
    description: 'AI-led opportunity portfolio showing what to optimise, what to automate, and where platform AI can deliver the greatest value.',
  },
  {
    id:          'aiExecutionStrategy',
    label:       'Execution Strategy',
    icon:        ListChecks,
    color:       '#0C66E4',
    description: 'Opinionated execution plan — prioritised actions, sequencing, phasing, dependencies, and what should wait.',
  },
  {
    id:          'automationPacks',
    label:       'AI & Automation Packs',
    icon:        Package,
    color:       '#0C66E4',
    description: 'Platform-specific automation and AI packs — Jira packs for Forge/Jira, or ServiceNow-native Flow Designer and Now Platform packs for ServiceNow.',
  },
  {
    id:          'frameworkAssessment',
    label:       'Framework Assessment',
    icon:        ShieldCheck,
    color:       '#0C66E4',
    description: 'Assess process maturity against selected best-practice frameworks such as ITIL, grounded in actual process evidence and operational patterns.',
  },

  {
    id:          'processInvestigations',
    label:       'Ask Advisor',
    icon:        Search,
    color:       '#0C66E4',
    description: 'Guided Q&A over the full Process Advisor data payload — metrics, variants, transitions, SLA signals, field quality, and sample evidence.',
  },
  {
    id:          'consultingReport',
    label:       'Assessment Report',
    icon:        FileText,
    color:       '#0C66E4',
    description: 'Final client-ready assessment report consolidating scope, evidence, Advisor insights, curated findings, actions, value analysis, and next steps.',
  },
  {
    id:          'consultingWorkspace',
    label:       'Assessment Dashboard',
    icon:        BookOpen,
    color:       '#0C66E4',
    description: 'Model-scoped command centre for the engagement brief, curated findings, validated actions, and final assessment outputs.',
  },
  {
    id:          'workbenchBrief',
    label:       'Engagement Brief',
    icon:        Users,
    color:       '#0C66E4',
    description: 'Scope, objective, stakeholders, assumptions, and client context for this engagement.',
  },
  {
    id:          'workbenchFindings',
    label:       'Findings',
    icon:        Lightbulb,
    color:       '#0C66E4',
    description: 'Review evidence-backed consultant findings and validate what should move forward.',
  },
  {
    id:          'workbenchActions',
    label:       'Actions',
    icon:        CheckSquare,
    color:       '#0C66E4',
    description: 'Convert validated findings into agreed implementation, automation, governance, or analysis work.',
  },
  {
    id:          'processSnapshot',
    label:       'Process Snapshot',
    icon:        LayoutDashboard,
    color:       '#5243AA',
    description: 'Live evidence view — full metrics dashboard, process flow diagram, variant breakdown, and transition analysis in a single client-ready page.',
  },
];





const ConsultantDashboard = ({
  AI, aiInsightsState, setAiInsightsState, license, licenseType,
  processLabel = 'Process', entityLabel = 'Work Item',
  initialTab = null, hideNav = false,
  snapshotCount: externalSnapshotCount, setSnapshotCount: setExternalSnapshotCount,
  snapshotPanelOpen: externalPanelOpen, setSnapshotPanelOpen: setExternalPanelOpen,
  customer = null, customerID = null,
}) => {

  const validTabIds = useMemo(() => new Set(TABS.map(t => t.id)), []);
  const getPersistedInitialTab = () => {
    try {
      const saved = window.localStorage.getItem('metricus.consultant.activeTab');
      return saved && validTabIds.has(saved) ? saved : null;
    } catch (_) {
      return null;
    }
  };
  const resolveInitial = (t) => (t && validTabIds.has(t)) ? t : (getPersistedInitialTab() || 'quickHealthcheck');
  const [activeTab, setActiveTab] = useState(resolveInitial(initialTab));

  // Sync when AppNav changes the sub-tab externally
  useEffect(() => {
    if (initialTab == null) return;
    if (!validTabIds.has(initialTab)) {
      // eslint-disable-next-line no-console
      console.warn('[ConsultantDashboard] Unknown initialTab from parent:', initialTab,
        '— falling back to quickHealthcheck. Valid ids:', Array.from(validTabIds));
      setActiveTab('quickHealthcheck');
      return;
    }
    setActiveTab(initialTab);
    try { window.localStorage.setItem('metricus.consultant.activeTab', initialTab); } catch (_) {}
  }, [initialTab, validTabIds]);

  // AppNav can sit outside this component when hideNav=true. Listen for its
  // explicit tab-change event as a belt-and-braces path so sub-tabs do not
  // depend solely on AppShell remount/prop timing.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (event) => {
      const tabId = event?.detail?.tabId;
      if (!tabId || !validTabIds.has(tabId)) return;
      try { window.localStorage.setItem('metricus.consultant.activeTab', tabId); } catch (_) {}
      setActiveTab(tabId);
    };
    window.addEventListener('metricus:consultant-tab-change', handler);
    return () => window.removeEventListener('metricus:consultant-tab-change', handler);
  }, [validTabIds]);

  const [refreshKey, setRefreshKey] = useState(0);
  const aiModelConfig = useMemo(() => getAIModelConfig(), []);
  const [_snapshotPanelOpen, _setSnapshotPanelOpen] = useState(false);
  const [_snapshotCount, _setSnapshotCount] = useState(0);
  // Use external state if provided by AppShell (so AppNav badge stays in sync)
  const snapshotPanelOpen    = externalPanelOpen    ?? _snapshotPanelOpen;
  const setSnapshotPanelOpen = setExternalPanelOpen ?? _setSnapshotPanelOpen;
  const snapshotCount        = externalSnapshotCount ?? _snapshotCount;
  const setSnapshotCount     = setExternalSnapshotCount ?? _setSnapshotCount;
  const activeTabMeta = useMemo(() => TABS.find((tab) => tab.id === activeTab) || TABS[0], [activeTab]);



  useEffect(() => {
    const modelId = getModelID();
    const AppVersion = getAppVersion();
    if (typeof window !== 'undefined' && window.__METRICUS_JIRA_PRODUCT_EDITION__ === 'free') {
      setSnapshotCount(0);
      return;
    }
    if (!modelId) return;
    listSnapshots({ modelId, AppVersion })
      .then((res) => setSnapshotCount((res.snapshots || []).length))
      .catch(() => {});
  }, [refreshKey]);





  React.useEffect(() => {
    const listener = () => setRefreshKey(prev => prev + 1);
    listener();
    apiProvider.on(Event.setActionBar_Consultant, listener);
    apiProvider.on(Event.updateDashboard, listener);
    return () => {
      apiProvider.off(Event.setActionBar_Consultant, listener);
      apiProvider.off(Event.updateDashboard, listener);
    };
  }, []);

  const sharedProps = { processLabel, entityLabel, refreshKey, aiModelConfig };

  const renderActivePanel = () => {
    if (activeTab === 'quickHealthcheck') return <QuickHealthcheck {...sharedProps} />;
    if (activeTab === 'processHealth') return <ProcessHealth {...sharedProps} />;
    if (activeTab === 'trendAnalysis') return <TrendAnalysis {...sharedProps} />;
    if (activeTab === 'aiIntelligence') return <AIIntelligence {...sharedProps} />;
    if (activeTab === 'aiExecutionStrategy') return <AIExecutionStrategy {...sharedProps} />;
    if (activeTab === 'automationPacks') return <AutomationPacks {...sharedProps} />;
    if (activeTab === 'frameworkAssessment') return <FrameworkAssessment {...sharedProps} />;
    if (activeTab === 'consultingReport') return <ConsultingReport {...sharedProps} />;
    if (activeTab === 'processInvestigations') return <ProcessInvestigations {...sharedProps} />;
    if (['consultingWorkspace', 'workbenchBrief', 'workbenchFindings', 'workbenchActions'].includes(activeTab)) {
      return (
        <ConsultingWorkspace
          {...sharedProps}
          initialSection={
            activeTab === 'workbenchBrief' ? 'brief'
              : activeTab === 'workbenchFindings' ? 'findings'
              : activeTab === 'workbenchActions' ? 'actions'
              : 'dashboard'
          }
        />
      );
    }
    if (activeTab === 'processSnapshot') return <ProcessSnapshot {...sharedProps} />;
    return null;
  };

  return (
    <div className="consultant-dashboard">

      <style jsx>{`
        .consultant-dashboard {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          color: #172B4D;
          background: #F4F5F7;
          border: 1px solid #DFE1E6;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(9,30,66,0.10), 0 0 1px rgba(9,30,66,0.08);
          overflow: hidden;
        }
        .consultant-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 12px 16px;
          background: #FFFFFF;
          border-bottom: 1px solid #DFE1E6;
          flex-wrap: wrap;
        }
        .ai-model-toolbar {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 10px;
          padding: 8px 16px;
          background: #FFFFFF;
          border-bottom: 1px solid #DFE1E6;
          font-size: 12px;
          color: #42526E;
        }
        .ai-model-toolbar select,
        .ai-model-toolbar input {
          height: 30px;
          border: 1px solid #DFE1E6;
          border-radius: 4px;
          background: #FFFFFF;
          color: #172B4D;
          font-size: 12px;
          padding: 0 8px;
        }
        .ai-model-toolbar input { width: 64px; }
        .tab-content {
          padding: 0;
          height: calc(100vh - 150px);
          overflow: hidden;
          background: #F4F5F7;
          display: flex;
          flex-direction: column;
        }
        .content-container {
          max-width: 1500px;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .tab-panel { display: none; }
        .tab-panel.active {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
        }
        .tab-content::-webkit-scrollbar { width: 8px; }
        .tab-content::-webkit-scrollbar-track { background: #EBECF0; border-radius: 4px; }
        .tab-content::-webkit-scrollbar-thumb { background: #C1C7D0; border-radius: 4px; }
        .tab-content::-webkit-scrollbar-thumb:hover { background: #B3BAC5; }
        @media print {
          .tab-content { height: auto; overflow: visible; padding: 0; }
        }
        @media (max-width: 768px) { .tab-content { padding: 0; } }
      `}</style>

      {/* AI model is configured once by the host application. */}

      {/* Internal tab navigation removed — AppNav handles sub-tab rendering
           and action buttons. hideNav prop retained for backward compat. */}

      <div className="tab-content">

        {/* Defensive: if activeTab doesn't match any known panel, surface it loudly
            instead of silently falling through to Process Health. */}
        {!validTabIds.has(activeTab) && (
          <div style={{
            margin: 12, padding: '12px 16px', borderRadius: 6,
            background: '#FFF7E6', border: '1px solid #FFB020', color: '#7A4100',
            fontSize: 13, fontWeight: 600,
          }}>
            ⚠️ Consultant Dashboard received an unknown tab id:{' '}
            <code style={{ background: '#FFE9B8', padding: '1px 6px', borderRadius: 3 }}>
              {String(activeTab)}
            </code>
            . Check AppShell → ConsultantDashboard <code>initialTab</code> prop and the
            <code>CONSULTANT_MODES</code> set. Valid ids:{' '}
            {Array.from(validTabIds).join(', ')}.
          </div>
        )}

        <div className="tab-panel content-container active">
          {renderActivePanel()}
        </div>

      </div>

      {/* Snapshot panel is rendered at AppShell level — no duplicate needed here */}

    </div>
  );
};

export default ConsultantDashboard;
